/**
 * OrgContextPane - the facts of a team or a department, always to hand.
 *
 * ONE COMPONENT FOR BOTH, deliberately. `TeamDetail` and `DepartmentDetails`
 * were the same 870-line file with a noun swapped: the same status badge, the
 * same four fact tiles, the same practice-area chips, the same avatar stack.
 * Keeping two copies is how the two pages drifted in the first place (one
 * reversed its feed, the other did not; one had a `document_count` tile the
 * other never showed). The noun is a prop.
 *
 * These facts read off the Details tab before, so they vanished the moment
 * someone opened the Feed or the Documents tab - which is exactly when "how
 * many people are on this, and is it still active" gets asked. As a pane they
 * stay put.
 *
 * IT STRETCHES. The pane resizes from 260 to 720, so the overview is an
 * `auto-fit` grid rather than a fixed column count - one column in a narrow
 * dock, two when it is pulled wide.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  PiCalendarBlankLight,
  PiCircleDashedLight,
  PiFileTextLight,
  PiUsersLight,
} from 'react-icons/pi';
import Badge from '../../../shared/TailwindComponents/Badge';
import Button from '../../../shared/TailwindComponents/Button';
import { Field, Well } from '../../../shared/brand/Field';
import { ContextSection } from '../../../shared/brand/ContextPane';
import CollaboratorStack from '../../../shared/access/CollaboratorStack';
import { formatDate } from '../../../../utils/functions';
import { toAccessRows } from './orgMembers';

export interface OrgContextRecord {
  name?: string;
  description?: string;
  is_active?: boolean;
  members?: any[];
  created_at?: string;
  practice_areas?: Array<{ id: number; name: string }>;
  document_count?: number;
}

interface Props {
  record: OrgContextRecord | null;
  /**
   * Raises the ACCESS PANE - not the manage side panel.
   *
   * The avatars answer "who is on this", which is a READ. Reading is the
   * pane's job; the pane's own footer button is the one way to write. This is
   * the same rule the project and matter pages follow.
   */
  onOpenAccess: () => void;
  /** Switches the canvas to the Documents tab. */
  onOpenDocuments: () => void;
}

const OrgContextPane: React.FC<Props> = ({ record, onOpenAccess, onOpenDocuments }) => {
  const { t } = useTranslation();

  if (!record) return null;

  const members = record.members || [];
  const documents = record.document_count || 0;

  return (
    <div className="flex flex-col h-full min-h-0 overflow-y-auto custom-scrollbar-main px-3 py-3 space-y-4">
      {/* Overview - the two-word answers, side by side. */}
      <div>
        <ContextSection>{t('Overview')}</ContextSection>
        <div
          className="grid gap-3"
          style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))' }}
        >
          <Field icon={<PiCircleDashedLight />} label={t('Status')}>
            <Badge color={record.is_active ? 'green' : 'gray'}>
              {record.is_active ? t('Active') : t('Inactive')}
            </Badge>
          </Field>
          <Field icon={<PiUsersLight />} label={t('Members')} value={members.length} />
        </div>
      </div>

      {/* Timeline. One stop for now - a team records only its creation. */}
      <div>
        <ContextSection>{t('Timeline')}</ContextSection>
        <Well>
          <Field
            icon={<PiCalendarBlankLight />}
            label={t('Created')}
            value={record.created_at ? formatDate(record.created_at) : undefined}
            emptyText="-"
          />
        </Well>
      </div>

      {/* People - the faces, and the way into the list that names them. */}
      <div>
        <ContextSection>{t('People')}</ContextSection>
        <Well>
          <Field icon={<PiUsersLight />} label={t('Members')}>
            {members.length > 0 ? (
              <CollaboratorStack
                collaborators={toAccessRows(members)}
                onClick={onOpenAccess}
                withAccessLabels={false}
                testId="org-context-access-stack"
              />
            ) : (
              <span className="text-sm text-fg-muted">{t('No members yet.')}</span>
            )}
          </Field>
        </Well>
      </div>

      {record.practice_areas && record.practice_areas.length > 0 && (
        <div>
          <ContextSection>{t('Practice Areas')}</ContextSection>
          <div className="flex flex-wrap gap-1.5">
            {record.practice_areas.map((pa) => (
              <Badge key={pa.id} color="blue">{pa.name}</Badge>
            ))}
          </div>
        </div>
      )}

      {/* One count, and the way to the tab that lists them. */}
      <div>
        <ContextSection>{t('Documents')}</ContextSection>
        <Well className="flex items-center justify-between">
          <span className="flex items-center gap-2 text-sm text-fg-body">
            <PiFileTextLight className="h-4 w-4 text-fg-muted" />
            {documents} {documents === 1 ? t('document') : t('documents')}
          </span>
          <Button variant="neutral" size="small" onClick={onOpenDocuments}>
            {t('View')}
          </Button>
        </Well>
      </div>

      {/* A quiet tail: the description is edited on the canvas, but it is the
          one fact a reader wants while looking at a Feed post. */}
      {record.description && (
        <div>
          <ContextSection>{t('Description')}</ContextSection>
          <p className="text-xs text-fg-body leading-relaxed whitespace-pre-wrap">
            {record.description}
          </p>
        </div>
      )}
    </div>
  );
};

export default OrgContextPane;
